import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {PrimaryButton} from "./Buttons.jsx";

function DropdownEditMenu({
  children,
  align,
  buttonText=null,
  disabled=false,
  naked=false,
  ...rest
}) {

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState(null);

  const trigger = useRef(null);
  const dropdown = useRef(null);

  // Close on click outside
  useEffect(() => {
    if (!dropdownOpen) return;
    
    const clickHandler = ({ target }) => {
      if (
        dropdown.current?.contains(target) || 
        trigger.current?.contains(target)
      ) return;
      setDropdownOpen(false);
    };
    
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  }, [dropdownOpen]);

  // Close on ESC key
  useEffect(() => {
    if (!dropdownOpen) return;
    
    const keyHandler = ({ keyCode }) => {
      if (keyCode === 27) setDropdownOpen(false);
    };
    
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  }, [dropdownOpen]);

  // Calculate dropdown position when opening
  useEffect(() => {
    if (dropdownOpen && trigger.current) {
      const calculatePosition = (useMeasuredHeight = false) => {
        if (!trigger.current) return;
        const rect = trigger.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const dropdownWidth = 192; // w-48 = 192px
        const spacing = 4; // mt-1 = 4px
        
        // Try to measure actual dropdown height if it exists, otherwise use estimate
        let dropdownHeight = 150; // Better estimate
        if (useMeasuredHeight && dropdown.current) {
          const dropdownRect = dropdown.current.getBoundingClientRect();
          dropdownHeight = dropdownRect.height;
        }
        
        // Check if we should position above or below
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        const positionAbove = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;
        
        // Calculate vertical position (fixed positioning uses viewport coordinates)
        const top = positionAbove 
          ? rect.top - dropdownHeight - spacing
          : rect.bottom + spacing;
        
        // Calculate horizontal position based on align prop
        const position = { top };
        
        if (align === 'right') {
          // Right align: position dropdown's right edge at trigger's right edge
          const right = viewportWidth - rect.right;
          // Check if dropdown would overflow left edge
          if (rect.right - dropdownWidth < 0) {
            position.left = spacing;
          } else {
            position.right = right;
          }
        } else {
          // Left align: position dropdown's left edge at trigger's left edge
          const left = rect.left;
          // Check if dropdown would overflow right edge
          if (rect.left + dropdownWidth > viewportWidth) {
            position.right = spacing;
          } else {
            position.left = left;
          }
        }
        
        setDropdownPosition(position);
      };
      
      // Calculate immediately with estimate
      calculatePosition(false);
      
      // Recalculate with measured height after a short delay to allow dropdown to render
      const measureTimeout = setTimeout(() => {
        calculatePosition(true);
      }, 10);
      
      // Also update on scroll/resize
      const handleScroll = () => calculatePosition(true);
      const handleResize = () => calculatePosition(true);
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      
      return () => {
        clearTimeout(measureTimeout);
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    } else {
      setDropdownPosition(null);
    }
  }, [dropdownOpen, align]);

  const menuIcon = (
    <svg className="w-8 h-8 fill-current" viewBox="0 0 32 32">
      <circle cx="16" cy="16" r="2" />
      <circle cx="10" cy="16" r="2" />
      <circle cx="22" cy="16" r="2" />
    </svg>
  );

  return (
    <div {...rest}>
      <div ref={trigger} className="inline-block">
        {naked ? (
          <button
            disabled={disabled}
            aria-haspopup="true"
            onClick={() => setDropdownOpen(!dropdownOpen)}
            aria-expanded={dropdownOpen}
            className="text-slate-400 hover:text-slate-500 dark:text-slate-500 dark:hover:text-slate-400 rounded-full"
          >
            <span className="sr-only">Menu</span>
            {buttonText || menuIcon}
          </button>
        ) : (
          <PrimaryButton
            disabled={disabled}
            aria-haspopup="true"
            onClick={() => setDropdownOpen(!dropdownOpen)}
            aria-expanded={dropdownOpen}
          >
            <span className="sr-only">Menu</span>
            {buttonText || menuIcon}
          </PrimaryButton>
        )}
      </div>
      {dropdownOpen && dropdownPosition && createPortal(
        <div
          className="w-48 min-w-36 bg-white dark:bg-theme-surface-accent border border-slate-200 dark:border-slate-700 py-1.5 rounded shadow-lg overflow-hidden fixed z-[60]"
          style={{
            top: `${dropdownPosition.top}px`,
            ...(dropdownPosition.left !== undefined && { left: `${dropdownPosition.left}px` }),
            ...(dropdownPosition.right !== undefined && { right: `${dropdownPosition.right}px` }),
          }}
        >
          <ul ref={dropdown}>
            {children}
          </ul>
        </div>,
        document.body
      )}
    </div>
  );
}

export default DropdownEditMenu;